import React, { useCallback, useEffect, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import * as aiChatApi from '../../services/aiChat';
import './ChatShare.css';
import { fmtDateTime } from '../../utils/ruDates';

const EXPIRY_OPTIONS = [
    { value: 1, label: '1 день' },
    { value: 7, label: '7 дней' },
    { value: 30, label: '30 дней' },
    { value: null, label: 'Без срока' },
];

const STATE_LABELS = { active: 'Активна', expired: 'Истекла', revoked: 'Отозвана' };

function formatDate(iso) {
    if (!iso) return '—';
    return fmtDateTime(iso);
}

async function copyText(text) {
    if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return;
    }
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
}

function ChatShareDialog({ sessionId, onClose }) {
    const { request } = useApi();
    const [expiry, setExpiry] = useState(7);
    const [links, setLinks] = useState([]);
    const [created, setCreated] = useState(null);
    const [copied, setCopied] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const loadLinks = useCallback(async () => {
        try {
            setLinks(await aiChatApi.listShares(request, sessionId) || []);
        } catch (err) {
            setError(err.message || 'Не удалось загрузить ссылки');
        }
    }, [request, sessionId]);

    useEffect(() => { loadLinks(); }, [loadLinks]);

    useEffect(() => {
        const onKey = (event) => { if (event.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const handleCreate = async () => {
        setBusy(true);
        setError(null);
        setCopied(false);
        try {
            const link = await aiChatApi.createShare(request, sessionId, expiry);
            setCreated(`${window.location.origin}${link.path}`);
            await loadLinks();
        } catch (err) {
            setError(err.message || 'Не удалось создать ссылку');
        } finally {
            setBusy(false);
        }
    };

    const handleCopy = async () => {
        if (!created) return;
        await copyText(created).catch(() => {});
        setCopied(true);
    };

    const handleRevoke = async (shareId) => {
        if (!window.confirm('Отозвать ссылку? По ней чат больше не откроется.')) return;
        try {
            await aiChatApi.revokeShare(request, shareId);
            await loadLinks();
        } catch (err) {
            setError(err.message || 'Не удалось отозвать ссылку');
        }
    };

    return (
        <div className="chat-share__overlay" onClick={onClose}>
            <div className="chat-share" role="dialog" aria-label="Поделиться чатом" onClick={(e) => e.stopPropagation()}>
                <div className="chat-share__head">
                    <h3>Поделиться чатом</h3>
                    <button type="button" className="ghost chat-share__close" onClick={onClose} aria-label="Закрыть">×</button>
                </div>

                <p className="chat-share__warning">
                    Любой, у кого есть ссылка, увидит весь этот чат — вопросы и ответы, включая цифры
                    кабинета, — в том числе сообщения, которые появятся позже. Редактировать чат по ссылке
                    нельзя. Страница не индексируется поисковиками.
                </p>

                <div className="chat-share__create">
                    <label className="chat-share__label" htmlFor="chat-share-expiry">Срок действия</label>
                    <select
                        id="chat-share-expiry"
                        value={expiry === null ? '' : String(expiry)}
                        onChange={(e) => setExpiry(e.target.value === '' ? null : Number(e.target.value))}
                    >
                        {EXPIRY_OPTIONS.map((opt) => (
                            <option key={opt.label} value={opt.value === null ? '' : String(opt.value)}>{opt.label}</option>
                        ))}
                    </select>
                    <button type="button" onClick={handleCreate} disabled={busy}>
                        {busy ? 'Создаю…' : 'Создать ссылку'}
                    </button>
                </div>

                {created && (
                    <div className="chat-share__result">
                        <input type="text" readOnly value={created} onFocus={(e) => e.target.select()} />
                        <button type="button" onClick={handleCopy}>{copied ? '✓ Скопировано' : 'Копировать'}</button>
                        <div className="chat-share__hint">Сохраните ссылку сейчас — позже её нельзя будет показать снова, только создать новую.</div>
                    </div>
                )}

                {error && <div className="chat-share__error">{error}</div>}

                <div className="chat-share__label">Ссылки на этот чат</div>
                {links.length === 0 ? (
                    <div className="chat-share__empty">Ссылок пока нет.</div>
                ) : (
                    <ul className="chat-share__list">
                        {links.map((link) => (
                            <li key={link.id} className={`chat-share__item chat-share__item--${link.state}`}>
                                <div>
                                    <div className="chat-share__item-state">
                                        {STATE_LABELS[link.state] || link.state}
                                        {link.revoked_by_admin && ' администратором'}
                                    </div>
                                    <div className="chat-share__item-meta">
                                        создана {formatDate(link.created_at)} · {link.expires_at ? `до ${formatDate(link.expires_at)}` : 'без срока'} · просмотров {link.view_count}
                                    </div>
                                </div>
                                {link.state === 'active' && (
                                    <button type="button" className="ghost" onClick={() => handleRevoke(link.id)}>Отозвать</button>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

export default ChatShareDialog;
